import { getFunctions, httpsCallable } from 'firebase/functions';

export type ProviderActivityDocument = {
    id: string;
    data: Record<string, any>;
};

type ProviderActivityResponse = {
    healthcareProviderId: string;
    documents: ProviderActivityDocument[];
};

const MAX_USER_IDS_PER_REQUEST = 500;

// Fetch activity in bounded callable requests so large Enterprise providers remain supported.
export async function fetchProviderClientActivityDocuments(
    healthcareProviderId: string,
    submittedUserIds: string[]
): Promise<ProviderActivityDocument[]> {
    const userIds = Array.from(new Set(
        submittedUserIds.map((userId) => userId.trim()).filter(Boolean)
    ));
    if (!healthcareProviderId || userIds.length === 0) {
        return [];
    }

    const callable = httpsCallable<
        { healthcareProviderId: string; userIds: string[] },
        ProviderActivityResponse
    >(getFunctions(), 'fetchProviderClientActivity');
    const requests: Array<Promise<{ data: ProviderActivityResponse }>> = [];
    for (let index = 0; index < userIds.length; index += MAX_USER_IDS_PER_REQUEST) {
        requests.push(callable({
            healthcareProviderId,
            userIds: userIds.slice(index, index + MAX_USER_IDS_PER_REQUEST),
        }));
    }

    const responses = await Promise.all(requests);
    const documents = new Map<string, ProviderActivityDocument>();
    responses.forEach((response) => {
        if (response.data.healthcareProviderId !== healthcareProviderId ||
            !Array.isArray(response.data.documents)) {
            throw new Error('Provider activity was returned for the wrong scope.');
        }
        response.data.documents.forEach((document) => {
            if (document && typeof document.id === 'string' && document.data &&
                typeof document.data === 'object') {
                documents.set(document.id, document);
            }
        });
    });
    return Array.from(documents.values());
}
